import { useState, useEffect } from "react";
import { campaignTemplates } from "@/mocks/campaignsData";
import { fetchApi } from "@/services/apiService";
import type { CampaignDraftData } from "../page";

interface Props {
  onBack: () => void;
  onNext: () => void;
  draft: CampaignDraftData;
  onUpdateDraft: (data: Partial<CampaignDraftData>) => void;
}

export default function StepMessage({ onBack, onNext, draft, onUpdateDraft }: Props) {
  const [channel, setChannel] = useState<"WhatsApp" | "SMS" | "Email">(
    (draft.channel as "WhatsApp" | "SMS" | "Email") || "WhatsApp"
  );
  const [selectedTemplate, setSelectedTemplate] = useState(campaignTemplates[0].id);
  const [customMessage, setCustomMessage] = useState(false);
  const [customText, setCustomText] = useState(draft.customText || "");
  const [liveAgents, setLiveAgents] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");

  useEffect(() => {
    fetchApi<any>("/auth/company-user/all", undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];
        const names: string[] = [];
        userList.forEach((u: any) => {
          const name = u.name || u.fullName || u.username;
          if (name && !names.includes(name)) names.push(name);
        });
        if (names.length > 0) {
          setLiveAgents(names);
          setSelectedAgent(names[0]);
        }
      })
      .catch(() => {});
  }, []);

  const template = campaignTemplates.find((t) => t.id === selectedTemplate) || campaignTemplates[0];

  const handleChannelSelect = (ch: "WhatsApp" | "SMS" | "Email") => {
    setChannel(ch);
    onUpdateDraft({ channel: ch });
  };

  const handleNext = () => {
    onUpdateDraft({
      channel,
      templateName: template.name,
      customText: customMessage ? customText : undefined,
    });
    onNext();
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4">What will you send?</h2>

        {/* Channel selector */}
        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-700 mb-2">Channel</label>
          <div className="flex items-center gap-2">
            {(["WhatsApp", "SMS", "Email"] as const).map((ch) => (
              <button
                key={ch}
                onClick={() => handleChannelSelect(ch)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  channel === ch
                    ? "bg-indigo-600 text-white shadow-xs"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {ch === "WhatsApp" && <i className="ri-whatsapp-line mr-1.5" />}
                {ch === "SMS" && <i className="ri-message-2-line mr-1.5" />}
                {ch === "Email" && <i className="ri-mail-line mr-1.5" />}
                {ch}
              </button>
            ))}
          </div>
        </div>

        {/* Template selector */}
        {!customMessage && (
          <>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-2">Use approved template (recommended)</label>
              <select
                value={selectedTemplate}
                onChange={(e) => {
                  setSelectedTemplate(e.target.value);
                  const sel = campaignTemplates.find((t) => t.id === e.target.value);
                  if (sel) onUpdateDraft({ templateName: sel.name });
                }}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
              >
                {campaignTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.status})
                  </option>
                ))}
              </select>
            </div>

            {/* Template preview */}
            <div className="bg-gray-900 rounded-2xl p-4 mb-4 shadow-inner">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center">
                  <i className="ri-user-line text-white text-xs" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">CRM Marketing Broadcast</p>
                  <p className="text-[10px] text-gray-400">{channel} Message Preview</p>
                </div>
              </div>
              <div className="bg-white rounded-xl rounded-tl-none p-3.5 max-w-[90%]">
                <p className="text-xs text-gray-800 whitespace-pre-line leading-relaxed">{template.body}</p>
                <p className="text-[10px] text-gray-400 mt-2 text-right">Just now</p>
              </div>
            </div>
          </>
        )}

        {/* Dynamic Live Agents mapping if available */}
        {!customMessage && liveAgents.length > 0 && (
          <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-3.5">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sender Agent Signature</label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {liveAgents.map((ag) => (
                <option key={ag} value={ag}>{ag}</option>
              ))}
            </select>
          </div>
        )}

        {/* Custom message toggle */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-900">Send custom message text</p>
              <p className="text-[11px] text-gray-500">Type custom text directly for recipients</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={customMessage}
              onClick={() => setCustomMessage(!customMessage)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                customMessage ? "bg-indigo-600" : "bg-gray-200"
              }`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  customMessage ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
          {customMessage && (
            <textarea
              value={customText}
              onChange={(e) => {
                setCustomText(e.target.value);
                onUpdateDraft({ customText: e.target.value });
              }}
              placeholder="Type your custom broadcast message here..."
              className="w-full mt-3 border border-gray-200 rounded-xl p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
              rows={4}
              maxLength={500}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-sm"
        >
          Next: Schedule <i className="ri-arrow-right-line" />
        </button>
      </div>
    </div>
  );
}